import React, { useMemo } from 'react';
import { Col, Form, Row } from 'react-bootstrap';

import MonthYearInput from './MonthYearInput';
import {
  EMPTY_STRUCTURED_FILTERS,
  PMO_PHASES,
  PMO_STATUS_FILTERS,
  PMO_VIGENCIA_FILTERS,
  PmoCarteiraGroup,
  PmoStructuredFiltersState,
  carteiraCatalog,
  classificationOptionsForCarteira,
} from '../../pages/projects/pmoSpecConfig';
import {
  normalizePeriodReportScope,
  PERIOD_WORKLOG_KPI_OPTIONS,
  PERIOD_WORKLOG_SCOPE_OPTIONS,
  COMPRAS_E_REPORT_PERIOD_HINT,
} from '../../pages/projects/pmoReportScope';
import { hasCompletePmoWorklogPeriod } from '../../pages/projects/pmoWorklogPeriodBounds';

interface ProjectsStructuredFiltersProps {
  value: PmoStructuredFiltersState;
  carteiraGroup: PmoCarteiraGroup;
  onChange: (next: PmoStructuredFiltersState) => void;
  disabled?: boolean;
  enabledPeriodKpiIds?: string[];
  onTogglePeriodKpi?: (kpiId: string) => void;
}

const ProjectsStructuredFilters: React.FC<ProjectsStructuredFiltersProps> = ({
  value,
  carteiraGroup,
  onChange,
  disabled = false,
  enabledPeriodKpiIds = [],
  onTogglePeriodKpi,
}) => {
  const carteiras = carteiraCatalog(carteiraGroup);
  const classificationOptions = useMemo(
    () => classificationOptionsForCarteira(value.carteiraId),
    [value.carteiraId],
  );
  const periodComplete = hasCompletePmoWorklogPeriod(value.periodStart, value.periodEnd);
  const selectedPeriodScope = PERIOD_WORKLOG_SCOPE_OPTIONS.find(
    (item) => item.id === value.reportScope,
  ) || PERIOD_WORKLOG_SCOPE_OPTIONS[0];

  const patch = (partial: Partial<PmoStructuredFiltersState>) => {
    onChange({ ...value, ...partial });
  };

  const handleCarteiraChange = (carteiraId: string) => {
    const nextOptions = classificationOptionsForCarteira(carteiraId);
    const classificationStillValid = !value.classificationValue
      || nextOptions.includes(value.classificationValue);
    patch({
      carteiraId,
      classificationValue: classificationStillValid ? value.classificationValue : '',
    });
  };

  return (
    <div className="projects-structured-filters">
      <Row className="g-2">
        <Col md={3}>
          <MonthYearInput
            label="Período inicial"
            placeholder="dd/mm/aaaa"
            value={value.periodStart}
            disabled={disabled}
            onChange={(periodStart) => patch({ periodStart })}
          />
        </Col>
        <Col md={3}>
          <MonthYearInput
            label="Período final"
            placeholder="dd/mm/aaaa"
            value={value.periodEnd}
            disabled={disabled}
            onChange={(periodEnd) => patch({ periodEnd })}
          />
        </Col>
        <Col md={3}>
          <Form.Label className="small mb-1">Carteira</Form.Label>
          <Form.Select
            size="sm"
            value={value.carteiraId}
            disabled={disabled}
            onChange={(event) => handleCarteiraChange(event.target.value)}
          >
            <option value="">Todas (geral + por carteira)</option>
            {carteiras.map((item) => (
              <option key={item.id} value={item.id}>
                {item.label}
              </option>
            ))}
          </Form.Select>
        </Col>
        <Col md={3}>
          <Form.Label className="small mb-1">Classificação do Atendimento</Form.Label>
          <Form.Select
            size="sm"
            value={value.classificationValue}
            disabled={disabled}
            onChange={(event) => patch({ classificationValue: event.target.value })}
          >
            <option value="">Todas</option>
            {classificationOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </Form.Select>
        </Col>
        <Col md={3}>
          <Form.Label className="small mb-1">Status (classificação PMO)</Form.Label>
          <Form.Select
            size="sm"
            value={value.statusFilter}
            disabled={disabled}
            onChange={(event) => patch({ statusFilter: event.target.value })}
          >
            <option value="">Todos</option>
            {PMO_STATUS_FILTERS.map((item) => (
              <option key={item.id} value={item.id}>
                {item.label}
              </option>
            ))}
          </Form.Select>
        </Col>
        <Col md={3}>
          <Form.Label className="small mb-1">Vigência</Form.Label>
          <Form.Select
            size="sm"
            value={value.vigencia}
            disabled={disabled}
            onChange={(event) => patch({ vigencia: event.target.value, phase: event.target.value === 'em_andamento' ? value.phase : '' })}
          >
            <option value="">Todas</option>
            {PMO_VIGENCIA_FILTERS.map((item) => (
              <option key={item.id} value={item.id}>
                {item.label}
              </option>
            ))}
          </Form.Select>
        </Col>
        <Col md={5}>
          <Form.Label className="small mb-1">Fase (em andamento)</Form.Label>
          <Form.Select
            size="sm"
            value={value.phase}
            disabled={disabled || value.vigencia !== 'em_andamento'}
            onChange={(event) => patch({ phase: event.target.value })}
          >
            <option value="">Todas as fases</option>
            {PMO_PHASES.map((phase) => (
              <option key={phase} value={phase}>
                {phase}
              </option>
            ))}
          </Form.Select>
        </Col>
        <Col md={4} className="d-flex align-items-end">
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            disabled={disabled}
            onClick={() => onChange({ ...EMPTY_STRUCTURED_FILTERS })}
          >
            Limpar filtros
          </button>
        </Col>
      </Row>
      <Row className="g-2 mt-1">
        <Col md={6}>
          <Form.Label className="small mb-1" htmlFor="projects-period-report-scope">
            Metodologia HH no período (De/Até)
          </Form.Label>
          <Form.Select
            id="projects-period-report-scope"
            size="sm"
            value={value.reportScope}
            disabled={disabled}
            onChange={(event) => {
              const reportScope = normalizePeriodReportScope(event.target.value);
              patch({
                reportScope,
                reportMode: reportScope !== 'carteira',
              });
            }}
          >
            {PERIOD_WORKLOG_SCOPE_OPTIONS.map((item) => (
              <option key={item.id} value={item.id}>
                {item.label}
              </option>
            ))}
          </Form.Select>
        </Col>
        <Col md={6} className="d-flex align-items-end">
          <p className="small text-muted mb-0 pb-1">
            {periodComplete
              ? `${selectedPeriodScope.hint} Afeta só os KPIs de HH no período; HH clássicos não mudam.`
              : 'Preencha Período inicial e Período final (dd/mm/aaaa ou mm/aaaa) para calcular os KPIs de período.'}
            {periodComplete && value.reportScope === 'planilha_export_worklogs' ? ` ${COMPRAS_E_REPORT_PERIOD_HINT}` : ''}
          </p>
        </Col>
        <Col md={12}>
          <Form.Label className="small mb-1">Exibir KPIs de período</Form.Label>
          <div className="projects-period-kpi-toggles">
            {PERIOD_WORKLOG_KPI_OPTIONS.map((item) => (
              <Form.Check
                key={item.id}
                inline
                type="checkbox"
                id={`projects-period-kpi-${item.id}`}
                label={item.label}
                checked={enabledPeriodKpiIds.includes(item.id)}
                disabled={disabled || !onTogglePeriodKpi}
                onChange={() => onTogglePeriodKpi?.(item.id)}
              />
            ))}
          </div>
          <p className="small text-muted mb-0">
            Marque para mostrar o card no dashboard. Vale na hora, sem recarregar a página.
          </p>
        </Col>
      </Row>
    </div>
  );
};

export default ProjectsStructuredFilters;
